import { endOfDay, startOfDay } from 'date-fns';
import { useUpdateTask } from 'features/tasks';
import React, { useMemo } from 'react';
import { TreeTaskCard } from 'shared/components/connected/task-card';
import {
  RenderItemProps,
  rootId,
  TreeItem,
  TreeItemTypes,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import {
  DayOrder,
  useUpdateTodoDaysOrderMutation,
} from 'shared/hooks/queries/use-update-todo-days-order-mutation';
import { ID } from 'shared/types/id';
import { overdueSectionID } from 'shared/types/orderable-section';
import { Task } from 'shared/types/task';
import { parseDateKey } from 'shared/utils/format-date-key';
import { getTaskPlanning } from 'shared/utils/get-task-planning';
import { createPlannedOccurrenceForDate } from 'shared/utils/schedules';

import { useDeadlineTree } from '../../hooks/use-deadline-tree';
import { GroupedListProps } from '../../types';
import { DeadlineHeader } from '../deadline-header';

const taskTreeItemTypes = [TreeItemTypes.Task];
const noDeadlineSectionID = 'no-deadline';

export const DeadlineGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
}) => {
  const today = useToday();
  const { submit: updateTask } = useUpdateTask();
  const { mutate: updateOrder } = useUpdateTodoDaysOrderMutation();

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const treeData = useDeadlineTree({
    tasks,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
  });

  const oldTreeData = useMemo(() => structuredClone(treeData), [treeData]);

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const updatedDocs: Task[] = [];
    const updatedSections: { id: ID; taskSorting: ID[] }[] = [];

    // Process each task to detect section changes
    updatedItems.forEach((item) => {
      if (item.type === TreeItemTypes.Task) {
        if (!tasksMap[item.id]) {
          return;
        }

        const task = tasksMap[item.id];
        const taskToUpdate = structuredClone(task);

        // Find which section this task is now in
        const newSectionItem = updatedItems.find((section) =>
          section.children?.includes(item.id),
        );
        const oldSectionItem = Object.values(oldTreeData).find((section) =>
          section.children?.includes(item.id),
        );

        if (!newSectionItem || !oldSectionItem) {
          return;
        }

        const newSectionId = newSectionItem.id;
        const oldSectionId = oldSectionItem.id;

        // Skip if task hasn't moved between sections
        if (newSectionId === oldSectionId) {
          return;
        }

        // Handle overdue section - tasks cannot be moved into overdue
        if (newSectionId === overdueSectionID) {
          return;
        }

        // Handle "No deadline" section - remove deadline
        if (newSectionId === noDeadlineSectionID) {
          // only remove deadline if task has no schedules
          if (!task.schedules?.length) {
            taskToUpdate.endStrategy = {
              deadline: null,
              completionCount: task.endStrategy?.completionCount ?? null,
            };
            updatedDocs.push(taskToUpdate);
          }
          return;
        }

        // Parse the new date from section ID
        const newDate = parseDateKey(newSectionId);
        const newDeadline = endOfDay(newDate);

        // Handle scheduled tasks - use mutation
        if (task.schedules?.length) {
          // Get the planned occurrence from the old section date
          const oldDate =
            oldSectionId === overdueSectionID
              ? endOfDay(new Date(0)) // Use epoch for overdue
              : parseDateKey(oldSectionId);

          const [plannedOccurrence] = getTaskPlanning(task, {
            startDate: startOfDay(oldDate),
            endDate: endOfDay(oldDate),
            weekStartsOn,
          });

          const schedules = createPlannedOccurrenceForDate(
            task.schedules,
            newDeadline,
            {
              timeslot: plannedOccurrence?.timeslot ?? null,
              originalDate: oldDate,
              weekStartsOn,
            },
          );

          updateTask({
            id: task.id,
            schedules,
          });
          return;
        }

        // Handle non-scheduled tasks - update deadline via onReorder
        taskToUpdate.endStrategy = {
          deadline: newDeadline,
          completionCount: taskToUpdate.endStrategy?.completionCount ?? null,
        };
        taskToUpdate.schedules = null;
        updatedDocs.push(taskToUpdate);
        return;
      }

      // Collect section updates for custom order
      if (item.type === TreeItemTypes.Date) {
        updatedSections.push({
          id: item.id,
          taskSorting: item.children ?? [],
        });

        // Update custom order for date sections (day sections only)
        if (item.id !== overdueSectionID && item.id !== noDeadlineSectionID) {
          const date = parseDateKey(item.id);
          const ordersToUpdate: DayOrder[] = [
            {
              date: endOfDay(date),
              ids: item.children ?? [],
            },
          ];

          updateOrder(ordersToUpdate);
        }
      }
    });

    onReorder(updatedSections, updatedDocs);
  };

  const renderItem: React.FC<RenderItemProps> = ({
    item,
    itemInstance,
    tree,
  }) => {
    const type = item.type;

    // Task
    if (type === TreeItemTypes.Task) {
      return tasksMap[item.sourceId] ? (
        <TreeTaskCard
          task={tasksMap[item.sourceId]}
          tree={tree}
          item={itemInstance}
          tasks={tasks}
        />
      ) : null;
    }

    // Date
    if (type === TreeItemTypes.Date) {
      return <DeadlineHeader item={itemInstance} />;
    }

    return null;
  };

  return (
    <VirtualizedTreeList
      rootItemId={rootId}
      items={treeData}
      renderItem={renderItem}
      scrollElement={scrollElement}
      multiselectTypes={taskTreeItemTypes}
      onReorder={_onReorder}
      collapsedItems={collapsedSections}
      onCollapse={onSectionCollapse}
    />
  );
};
